import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, usePropertyField } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import type { PhotoGallery } from '@vibesites/schema';

// ============================================================================
// Smart photo selection
// ============================================================================

function photoText(photo: PhotoGallery): string {
  const parts: string[] = [];

  if (photo.title) parts.push(photo.title);
  if (photo.description) parts.push(photo.description);
  if (photo.alt_text) parts.push(photo.alt_text);

  if (photo.image) {
    try { parts.push(decodeURIComponent(new URL(photo.image).pathname)); } catch { /* ignore */ }
  }
  if (photo.original_url) {
    try { parts.push(decodeURIComponent(new URL(photo.original_url).pathname)); } catch { /* ignore */ }
  }
  if (photo.source_page_urls) parts.push(photo.source_page_urls);

  return parts.join(' ').toLowerCase();
}

function scorePhoto(text: string, keywords: string[]): number {
  return keywords.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

function presetSlot(preset: string): number {
  let h = 0;
  for (let i = 0; i < preset.length; i++) {
    h = ((h << 5) - h + preset.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  keywords: string[],
  preset: string,
): number {
  if (!gallery.length) return -1;

  let bestIdx = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    const score = scorePhoto(photoText(gallery[i]), keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  if (bestIdx >= 0) return bestIdx;

  return presetSlot(preset) % gallery.length;
}

// ============================================================================
// Per-page keyword presets
// ============================================================================

const PAGE_PHOTO_KEYWORDS: Record<string, string[]> = {
  'floor-plans':          ['interior', 'apartment', 'unit', 'kitchen', 'bedroom', 'living', 'bathroom', 'home', 'floor', 'hardwood', 'floorplan', 'model'],
  'amenities':            ['pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities', 'recreation', 'courtyard', 'grill', 'dog-park', 'playground', 'spa', 'rooftop', 'yoga'],
  'gallery':              ['community', 'building', 'property', 'exterior', 'entrance', 'lobby', 'welcome', 'front', 'overview', 'aerial'],
  'neighborhood':         ['aerial', 'drone', 'skyline', 'neighborhood', 'exterior', 'bird', 'view', 'surrounding', 'location', 'city', 'street', 'map', 'area'],
  'virtual-tour':         ['interior', 'apartment', 'unit', 'bedroom', 'living', 'kitchen', 'tour', 'model', 'inside', 'walkthrough'],
  'contact':              ['leasing', 'office', 'lobby', 'entrance', 'front', 'welcome', 'building', 'exterior', 'reception', 'concierge'],
  'schedule-a-tour':      ['leasing', 'office', 'lobby', 'entrance', 'front', 'welcome', 'building', 'tour', 'reception'],
  'reviews':              ['community', 'building', 'property', 'exterior', 'entrance', 'resident', 'lifestyle', 'people'],
  'pet-policy':           ['dog', 'pet', 'park', 'paw', 'courtyard', 'exterior', 'community', 'amenity', 'bark', 'animal'],
  'future-residents-club': ['community', 'exterior', 'courtyard', 'building', 'property', 'amenities', 'recreation', 'outdoor', 'park', 'lifestyle', 'resident', 'welcome'],
  'fees':                 ['interior', 'apartment', 'unit', 'kitchen', 'bedroom', 'living', 'model', 'home', 'detail'],
  'resident-access':      ['building', 'exterior', 'entrance', 'lobby', 'property', 'community', 'resident', 'portal'],
  'interactive-map':      ['aerial', 'drone', 'skyline', 'neighborhood', 'exterior', 'bird', 'view', 'city', 'map', 'surrounding'],
  'privacy-policy':        ['building', 'exterior', 'entrance', 'lobby', 'property', 'community', 'leasing', 'office'],
  'sitemap':               ['building', 'exterior', 'community', 'property', 'entrance', 'aerial', 'overview'],
  'apply-now':             ['interior', 'apartment', 'unit', 'kitchen', 'bedroom', 'living', 'model', 'home'],
  'thank-you':             ['community', 'building', 'exterior', 'property', 'entrance', 'lobby', 'welcome', 'lifestyle'],
  'deals':                 ['interior', 'living', 'furniture', 'sofa', 'apartment', 'unit', 'bedroom', 'kitchen', 'home', 'furnished', 'lounge', 'model'],
  'dallas-mavericks-games': ['community', 'lifestyle', 'recreation', 'outdoor', 'courtyard', 'building', 'property', 'resident', 'amenities', 'park'],
};

// ============================================================================
// Component
// ============================================================================

interface PageHeroProps {
  pageTitle?: string;
  /** Unique prefix so each page's hero tokens are independently editable */
  tokenPrefix?: string;
  photoPreset?: string;
  photoKeywords?: string[];
}

export function PageHero({ pageTitle, tokenPrefix, photoPreset, photoKeywords }: PageHeroProps) {
  const titlePath = tokenPrefix ? `${tokenPrefix}.title` : 'pagehero.title';
  const subtitlePath = tokenPrefix ? `${tokenPrefix}.subtitle` : 'pagehero.subtitle';
  const bgImagePath = tokenPrefix ? `${tokenPrefix}.bg_image` : 'pagehero.bg_image';

  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const photoIndex = useMemo(() => {
    const keywords = photoKeywords
      || (photoPreset && PAGE_PHOTO_KEYWORDS[photoPreset])
      || [];
    if (!keywords.length && !photoPreset) return 0;
    return findBestPhotoIndex(gallery, keywords, photoPreset || 'default');
  }, [gallery, photoPreset, photoKeywords]);

  const heroImageUrl = useMemo(() => {
    if (photoIndex >= 0 && gallery[photoIndex]?.image) return gallery[photoIndex].image;
    if (featuredImage) return featuredImage;
    if (gallery.length && gallery[0]?.image) return gallery[0].image;
    return null;
  }, [gallery, photoIndex, featuredImage]);

  const bgImageDef = useMemo<SlotGroupDefinition>(() => ({
    label: 'Background Image',
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: photoIndex >= 0
          ? `photo_gallery[${photoIndex}].image`
          : 'property_featured_image',
      },
      alt: { label: 'Alt Text', type: 'text', defaultValue: pageTitle || 'Page banner' },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: Math.max(photoIndex, 0),
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  }), [photoIndex, pageTitle]);

  const bgImage = useSlotGroup(bgImagePath, bgImageDef);
  const defaultTitle = pageTitle || 'Welcome';
  const displayImage = bgImage.image || heroImageUrl;

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath={bgImagePath} definition={bgImageDef}>
        <div className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={bgImage.alt || pageTitle || ''}
              fill
              objectFit="cover"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
          <div className="absolute inset-0 bg-black/40" />
        </div>
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent
          path={titlePath}
          label="Title"
          defaultValue={`<p>${defaultTitle}</p>`}
          as="div"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0"
        />
        <RichTextContent
          path={subtitlePath}
          label="Subtitle"
          defaultValue=""
          as="p"
          className="font-body font-light text-base sm:text-xl md:text-2xl leading-relaxed opacity-90 drop-shadow-md max-w-3xl [&_p]:m-0"
        />
      </div>

      {/* Schema-lint anchors for static analysis — hidden at runtime when a page-specific prefix is used */}
      {tokenPrefix ? (
        <div className="hidden" style={{ display: 'none' }}>
          <RichTextContent path="pagehero.title" label="Title" defaultValue="<p>Welcome</p>" as="div" />
          <RichTextContent path="pagehero.subtitle" label="Subtitle" defaultValue="" as="p" />
        </div>
      ) : null}
      <EditableSlotGroup groupPath="pagehero.bg_image" definition={bgImageDef}>
        <span className="hidden" style={{ display: 'none' }} />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Welcome</p>',
    },
    'pagehero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'pagehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PageHero as any).contentSchema = contentSchema;

export default PageHero;
